import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { commonCss } from "../../StyleConfig/CommonCss";
import LoginBg from "../../Images/login/Loginbg.jpeg";
import PSPLOGO from "../../Images/Nav/PSPLOGO.png";

const GRID_PATTERN =
  "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')";

const blobKeyframes = {
  "@keyframes blob": {
    "0%": { transform: "translate(0px, 0px) scale(1)" },
    "33%": { transform: "translate(30px, -50px) scale(1.1)" },
    "66%": { transform: "translate(-20px, 20px) scale(0.9)" },
    "100%": { transform: "translate(0px, 0px) scale(1)" },
  },
};

const pulseKeyframes = {
  "@keyframes authPulse": {
    "0%, 100%": { opacity: 0.7 },
    "50%": { opacity: 0.4 },
  },
};

const blobBase = {
  position: "absolute",
  borderRadius: "50%",
  mixBlendMode: "screen",
  filter: "blur(80px)",
  opacity: 0.12,
  width: "18rem",
  height: "18rem",
  ...blobKeyframes,
};

/**
 * Animated glow-card shell for the auth pages.
 * - title / subtitle render under the brand logo
 * - children is the form
 * - plain: renders only children (used for the in-app embedded reset dialog)
 */
const AuthShell = ({ title, subtitle, children, plain = false, maxWidth = "560px" }) => {
  if (plain) {
    return (
      <Box sx={{ width: "100%" }}>
        <Header title={title} subtitle={subtitle} />
        {children}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        p: 2,
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.55)), url(${LoginBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Animated blobs */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden" }}>
        <Box sx={{ ...blobBase, top: "5rem", left: "2.5rem", backgroundColor: commonCss.blue, animation: "blob 7s infinite" }} />
        <Box sx={{ ...blobBase, top: "10rem", right: "2.5rem", backgroundColor: commonCss.cardNewColor, animation: "blob 7s infinite 2s" }} />
        <Box sx={{ ...blobBase, bottom: "-8rem", left: "33%", backgroundColor: commonCss.darkBlue, animation: "blob 7s infinite 4s" }} />
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: GRID_PATTERN, opacity: 0.3 }} />
      </Box>

      {/* Card with glow */}
      <Box sx={{ position: "relative", zIndex: 10, width: "100%", maxWidth }}>
        <Box
          sx={{
            ...pulseKeyframes,
            position: "absolute",
            inset: "-2px",
            background: `linear-gradient(to right, ${commonCss.blue}, ${commonCss.cardNewColor}, ${commonCss.darkBlue})`,
            borderRadius: "1.5rem",
            filter: "blur(6px)",
            opacity: 0.6,
            animation: "authPulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite",
          }}
        />
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(20px)",
            borderRadius: "1.5rem",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            boxShadow:
              "0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 1px rgba(255, 255, 255, 0.5) inset",
            p: { xs: "1.5rem", sm: "2.5rem" },
          }}
        >
          <Header title={title} subtitle={subtitle} />
          {children}
        </Paper>
      </Box>
    </Box>
  );
};

const Header = ({ title, subtitle }) => (
  <Box sx={{ textAlign: "center", mb: "1.75rem" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.9rem",
        mb: "1rem",
      }}
    >
      <img
        src={PSPLOGO}
        alt="logo"
        style={{
          width: 54,
          height: 54,
          objectFit: "contain",
          filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))",
        }}
      />
      <Typography
        noWrap
        sx={{
          fontSize: "1.6rem",
          fontWeight: 700,
          letterSpacing: "0.5px",
          color: commonCss.blue,
          textShadow: "0 1px 2px rgba(0, 0, 0, 0.12)",
        }}
      >
        e-Permit to Work
      </Typography>
    </Box>
    {title && (
      <Typography
        sx={{
          fontSize: "1.6rem",
          fontWeight: 800,
          color: "#1f4374",
          letterSpacing: "-0.02em",
        }}
      >
        {title}
      </Typography>
    )}
    {subtitle && (
      <Typography sx={{ color: "#64748b", fontSize: "0.92rem", fontWeight: 500, mt: 0.5 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

export const authFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: commonCss.inputBackgroundColor,
    borderRadius: commonCss.inputBorderRadius,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: commonCss.inputBorderColor,
  },
};

export const authButtonSx = (isLoading) => ({
  py: "0.6rem",
  mt: "0.5rem",
  color: commonCss.buttonTextColor,
  // fontWeight: 600,
  borderRadius: commonCss.buttonBorderRadius,
  textTransform: "none",
  fontSize: "0.9rem",
  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.15)",
  background: isLoading ? "default" : commonCss.blue,
  "&:hover": {
    backgroundColor: isLoading ? "default" : commonCss.darkBlue,
  },
  "&.Mui-disabled": { opacity: 0.6, color: commonCss.buttonTextColor },
});

export const authLabelSx = {
  fontWeight: commonCss.labelFontWeight,
  color: commonCss.labelInputColor,
};

export default AuthShell;
